import React, {useEffect, useMemo} from 'react';
import {Pages} from "../components/layout";
import BackendForm from "../components/forms/BackendForm";
import {useNavigate} from "react-router";
import {routes} from "../utils";
import Description from "../components/Description";
import {Link, useSearchParams} from "react-router-dom";
import SignUpFields, {signUpCodeName, signUpInitialValues} from "./partials/SignUpFields";
import {backendEndpoints} from "../libs/backendEndpoints";
import useAuth from "../components/hooks/useAuth";
import useTitle from "../components/hooks/useTitle";
import {concepts, Course, Group, ShareRequest} from "fearless-common";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Login from "./Login";
import FormFiller from "../components/forms/FormFiller";
import {Alert} from "fearless-common-ui";

const signupFormId = "sign-up-form";
const signUpAnswers = [
    {
        title: 'new @divvly',
        answers: [
            { name: 'username', value: `user_${Date.now()}` },
            { name: 'first_name', value: `FakeName` },
            { name: 'last_name', value: `FakeSurname` },
            { name: 'organisation', value: `FakeOrganisation` },
            { name: 'password', value: `password` },
            { name: 'password_copy', value: `password` },
            { name: 'email', value: `user_${Date.now()}@divvly.com` },
        ],
    },
]

function MessageWrapper({ message }: { message: string}) {
    return (
      <>
        <Alert variant="success">{message}</Alert>
      </>
    );
}

export default function SignUp({ group, course, shareRequests, redirect }: {
    group?: Group;
    course?: Course;
    shareRequests?: ShareRequest[];
    /** redirection url */
    redirect?: string;
}) {
    useTitle('Try it for free!')
    const navigate = useNavigate()
    const { user } = useAuth()
    const [sp] = useSearchParams();
    const userFirstName =  sp?.get('userFirstName');

    useEffect(() => {
        if (user) navigate(routes.landing);
    }, [user])

    const showForShareRequests = shareRequests?.length;
    const showForGroup = Boolean(group);

    const groupId = group?.id;
    const referralId = sp.get('referralId');
    const referralCode = sp.get('referralCode');
    const signUpCode = sp.get(signUpCodeName);
    const initialValues = useMemo(() => ({
        email: '',
        ...signUpInitialValues.Account,
        ...signUpInitialValues.UsernamePassword,
        groups: groupId ? [groupId] : [],
        custom_fields: {},
        referral_link_id: referralId,
        referral_link_code: referralCode,
        [signUpCodeName]: signUpCode,
    }), [groupId, referralCode, referralId, signUpCode]);

    const form = (
      <BackendForm
        targetMethod="POST"
        enableReinitialize
        onSuccess={() => navigate(routes.signUpSuccess)}
        targetEndpoint={backendEndpoints.signUp}
        initialValues={initialValues}
        // Enable remember form to lower the friction to sign up
        rememberForm
        formId={signupFormId}
      >
          <h3>{showForGroup ? 'Sign up' : 'Create login'}</h3>
          <SignUpFields.UsernamePassword checkDuplicate/>
          <br/>
          <SignUpFields.Account showEmail/>
          <SignUpFields.InstituteFields/>
          <br/>
          {referralCode ? (
            <MessageWrapper message={sp.get('referralMessage') as string} />
          ) : <SignUpFields.SignUpCode />}

          <FormFiller answerSets={signUpAnswers} formId={signupFormId}/>
      </BackendForm>
    );

    return (
        <Pages.Centred width={showForGroup ? 'sm' : "xs"} padded className="position-relative">
            <h1 className="text-center">
                {showForGroup && `Join ${group?.name}`}
                {showForShareRequests && `Access the ${shareRequests.length} shared files `}
                {!showForGroup && !showForShareRequests && 'Try for free!'}
            </h1>
            {group && userFirstName && (
                <Description className="mb-0 text-center mx-auto">
                    You have been invited by <b>{userFirstName}</b> to join the {concepts.Group.toLowerCase()}.
                </Description>
            )}
            {!showForGroup && (
                <Description className="text-center">
                    Have an account already? Sign in&nbsp;
                    <Link className="btn-link" to={redirect ? routes.loginWithRedirect(redirect) : routes.login}>here</Link>.
                </Description>
            )}
            <div className="mt-5">
                {showForGroup
                    ? (
                        <Row>
                            <Col md={6} className="border-gray border-end pe-sm-5">
                                <h3>Login</h3>
                                <Login.Form redirectUrl={window.location.href} />
                            </Col>
                            <Col md={6} className="ps-sm-5">
                                {form}
                            </Col>
                        </Row>
                    ) : form}
            </div>
        </Pages.Centred>
    );
}
